import React, { useState } from "react";
import { Compass, Briefcase, Plane, Check } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { postViewing } from "../lib/api";

const EXPERIENCE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d4e9e42c-b36d-4cef-b2b6-e711b533d4ca/images/194de134a44490f5476d21cc65ea42b4024d5fc30ca52b987e89c5c9a533120f.png";

export default function Services() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    locations: "",
    budget: "",
    notes: "",
  });
  const [state, setState] = useState(""); // sending|ok|error

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await postViewing(form);
      setState("ok");
      setForm({
        name: "",
        email: "",
        phone: "",
        preferred_date: "",
        locations: "",
        budget: "",
        notes: "",
      });
    } catch {
      setState("error");
    }
  };

  return (
    <div className="bg-charcoal pt-32" data-testid="page-services">
      {/* Header */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
          <p className="overline">{t.services.overline}</p>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-cream leading-[1.05] mt-4 max-w-4xl">
            {t.services.title}
          </h1>
          <span className="divider-gold mt-8" />
        </div>
      </section>

      {/* Three pillars */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-8">
          {[
            { icon: Compass, ...t.services.acquisition },
            { icon: Briefcase, ...t.services.investment },
            { icon: Plane, ...t.services.relocation },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className="border border-white/10 p-8 md:p-10 hover:border-gold/40 transition-colors"
                data-testid={`service-${i}`}
              >
                <Icon size={26} strokeWidth={1} className="text-gold" />
                <h3 className="font-serif text-3xl text-cream mt-6">{s.title}</h3>
                <p className="text-cream-muted text-sm mt-4 leading-relaxed font-light">
                  {s.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Viewing Experience */}
      <section
        id="viewing-experience"
        className="py-24 md:py-32 bg-charcoal-surface"
        data-testid="viewing-experience"
      >
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="overline">Signature Service</p>
            <h2 className="font-serif text-4xl md:text-6xl text-cream leading-tight mt-3">
              {t.services.experienceTitle}
            </h2>
            <p className="font-serif text-5xl text-gold mt-4">
              {t.services.experiencePrice}
            </p>
            <p className="text-cream-muted text-lg italic mt-3 font-light">
              {t.services.experienceTagline}
            </p>
            <span className="divider-gold mt-8" />
            <p className="text-cream-muted text-base leading-relaxed mt-8 max-w-xl">
              {t.services.experienceFull}
            </p>
            <ul className="mt-8 space-y-3 max-w-xl">
              {[
                t.services.experienceInclude1,
                t.services.experienceInclude2,
                t.services.experienceInclude3,
                t.services.experienceInclude4,
              ].map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-cream">
                  <Check size={18} strokeWidth={1.25} className="text-gold mt-1 shrink-0" />
                  <span className="text-sm font-light">{it}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 prop-img-wrap aspect-[16/10] overflow-hidden hidden lg:block">
              <img src={EXPERIENCE_IMG} alt="Luxury SUV viewing experience" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Form */}
          <div className="border border-gold/30 bg-charcoal p-8 md:p-10 sticky top-28">
            <p className="overline">Réservation</p>
            <h3 className="font-serif text-3xl text-cream leading-tight mt-3 mb-7">
              {t.services.bookTitle}
            </h3>

            {state === "ok" ? (
              <p className="text-gold text-sm border border-gold/40 p-4" data-testid="viewing-success">
                {t.services.bookSuccess}
              </p>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="viewing-form">
                <input
                  required
                  placeholder={t.detail.name}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="input-luxury"
                  data-testid="viewing-name"
                />
                <input
                  required
                  type="email"
                  placeholder={t.detail.email}
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="input-luxury"
                  data-testid="viewing-email"
                />
                <input
                  required
                  placeholder={t.detail.phone}
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="input-luxury"
                  data-testid="viewing-phone"
                />
                <div>
                  <p className="text-cream-subtle text-xs uppercase tracking-[0.18em] mb-1">
                    {t.services.preferredDate}
                  </p>
                  <input
                    required
                    type="date"
                    value={form.preferred_date}
                    onChange={(e) => setForm((f) => ({ ...f, preferred_date: e.target.value }))}
                    className="input-luxury"
                    data-testid="viewing-date"
                  />
                </div>
                <input
                  placeholder={t.services.locations}
                  value={form.locations}
                  onChange={(e) => setForm((f) => ({ ...f, locations: e.target.value }))}
                  className="input-luxury"
                  data-testid="viewing-locations"
                />
                <input
                  placeholder={t.services.budget}
                  value={form.budget}
                  onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                  className="input-luxury"
                  data-testid="viewing-budget"
                />
                <textarea
                  rows={3}
                  placeholder={t.services.notes}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="input-luxury resize-none"
                  data-testid="viewing-notes"
                />
                {state === "error" && (
                  <p className="text-red-400 text-xs">{t.services.bookError}</p>
                )}
                <button
                  type="submit"
                  disabled={state === "sending"}
                  className="btn-gold-solid w-full disabled:opacity-60"
                  data-testid="viewing-submit"
                >
                  {t.services.submit}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
